/// Advanced fix techniques — Day Skipper + Coastal Skipper extras
/// beyond the basic 3-point fix in `fix.ts`. Three techniques:
///
///   1. Two-bearings + a transit — hybrid fix using one transit
///      (highly accurate) plus a single bearing.
///   2. Running fix — single object observed at two times with
///      course + distance run between, projecting one position line
///      forward to intersect the second.
///   3. Doubling the angle on the bow — special-angle fix using
///      relative bearing geometry to derive distance off without
///      a compass measurement.

// ───────────────────────────────────────────────────────────────────
// 1. Two bearings + a transit
// ───────────────────────────────────────────────────────────────────

export interface AdvancedFixTechnique {
  id: "two-bearings-transit" | "running-fix" | "doubling-angle";
  name: string;
  /// One-line gist.
  summary: string;
  /// When this technique is the right choice.
  whenToUse: string;
  /// Step-by-step procedure.
  steps: readonly string[];
  /// Accuracy + reliability notes.
  accuracy: string;
  /// Pitfalls.
  pitfalls: readonly string[];
}

export const ADVANCED_FIXES: readonly AdvancedFixTechnique[] = Object.freeze([
  {
    id: "two-bearings-transit",
    name: "Two bearings + a transit",
    summary:
      "One transit (two charted objects in line — highly accurate, no compass error) PLUS one compass bearing to a third object. Faster + more accurate than three bearings.",
    whenToUse:
      "When a transit + a third charted mark are available simultaneously. Common at harbour entrances + along coasts with multiple charted features.",
    steps: [
      "Identify a TRANSIT — two charted objects in line. Note the moment they line up.",
      "Immediately take a compass bearing to a third object (the further the better).",
      "Apply variation + deviation to convert to true bearing.",
      "Plot the transit line on the chart (a line through the two transit objects).",
      "Plot the reciprocal of the true bearing FROM the third object — a line on the chart.",
      "The intersection of the transit line + the bearing line = your fix.",
      "Mark time + position; circle the fix; record the transit + bearing used.",
    ],
    accuracy:
      "Highly accurate — the transit is a near-perfect position line (no compass error), and the single bearing gives a position along it. Better than three bearings if the angle between the transit + the bearing is close to 90°.",
    pitfalls: [
      "Mis-identifying the transit — most-common student error. Double-check what's actually in line.",
      "Choosing a third object with a shallow angle to the transit — fix is uncertain perpendicular to that direction.",
      "Time delay between transit observation + bearing — at fast speeds the boat has moved before the bearing is taken.",
    ],
  },
  {
    id: "running-fix",
    name: "Running fix (single object, two times)",
    summary:
      "ONE charted object observed at TWO times. Plot the first position line + advance it by the boat's distance + course run between observations; intersection with the second position line = the fix.",
    whenToUse:
      "When only ONE charted feature is visible. Common offshore + along coasts with few landmarks. Pre-electronic-fix navigation favourite.",
    steps: [
      "Identify a SINGLE charted feature visible from your position.",
      "Take + record bearing 1 at time t1. Note your speed + course at this moment.",
      "Continue on course for at least 20-30 minutes (longer = more accuracy).",
      "Take + record bearing 2 at time t2.",
      "Plot bearing 1 from the object — it's a position line through your t1 position.",
      "From any point ON the bearing 1 line, plot the boat's COURSE + DISTANCE run during (t2-t1). The end of this line is the 'advanced' position of t1.",
      "Plot a line PARALLEL to the bearing 1 line, through the advanced position. This is the 'advanced bearing 1' position line.",
      "Plot bearing 2 from the object — it crosses the advanced bearing 1 line at your CURRENT (t2) position.",
      "Mark t2 + the fix at the intersection.",
    ],
    accuracy:
      "Moderate. Accuracy depends on KNOWING your course + distance correctly + on the angle between bearings being substantial (ideally 45-90°). Tidal stream + leeway error must be added in to the course + distance.",
    pitfalls: [
      "Not accounting for tidal stream — fix is wrong by the amount of unaccounted-for set.",
      "Bearings taken too close in time — angle too shallow + fix uncertain.",
      "Course or speed mis-estimated — directly causes fix error.",
      "Mis-identifying the object between the two observations — fix is meaningless.",
    ],
  },
  {
    id: "doubling-angle",
    name: "Doubling the angle on the bow",
    summary:
      "Special case of the running fix using relative bearings. When a charted object's relative bearing DOUBLES, the distance run from the first observation equals the distance off the object.",
    whenToUse:
      "Pre-electronic fix; quick distance-off estimation along a course; check on a chartplotter.",
    steps: [
      "Take a relative bearing to a charted object as you sail past it (typical bearings: 30°, 45°).",
      "Note your speed + start a timer.",
      "Continue on course UNCHANGED.",
      "When the relative bearing has DOUBLED (e.g. 30° → 60°, or 45° → 90°), the distance run from the first observation equals the distance off the object.",
      "Distance off = speed × time elapsed.",
      "Plot a position line from the object perpendicular to your course at that distance.",
    ],
    accuracy:
      "Useful for quick distance-off estimation; less accurate than a full running fix because it relies on relative bearings (which the boat's heading affects) + on holding an unchanged course.",
    pitfalls: [
      "Course changes between observations — invalidates the technique.",
      "Forgetting to start the timer — no elapsed time, no distance.",
      "Tidal stream perpendicular to the course — adds to the distance error.",
    ],
  },
]);
